import { useExtraGuaranteeExcluded } from '../useExtraGuaranteeExcluded';
import type { LineItem, ProductVariant } from '~~/types/commercetools';
import virtualProductSku from '~~/constants/virtualProductSku';

import { STANDARD_PRODUCT } from '~~/constants/productType';
import {
  SKU_FOR_EXTRA_GUARANTEE,
  PRODUCT_NAME_FOR_EXTRA_GUARANTEE,
  PRODUCT_IMAGE_FOR_EXTRA_GUARANTEE,
  PRODUCT_PRICE_FOR_EXTRA_GUARANTEE,
} from '~~/constants/extraGuarantee';
import { getLineItemFromSku } from '~~/helpers/cart/getLineItemFromSku';
import { isVirtualProduct } from '~~/helpers/isVirtualProduct';

type SetExtraGuarenteeInfoParams = {
  fullProductPrice: number | undefined;
  name: string;
  image: string;
  sku: string;
};

export const useExtraGuarantee = () => {

  const { cart } = useCartExtended();
  const { isOnPaymentPage, isThankYouPage } = useIsPage();

  const {
    isGuaranteeExcluded,
  } = useExtraGuaranteeExcluded();

  // TO DO INSP-4165 - Create useExtraGuarantee store
  const skuForExtraGuarantee: Ref<string | null> = useState(SKU_FOR_EXTRA_GUARANTEE, () => null);
  const productNameForExtraGuarantee: Ref<string | null> = useState(PRODUCT_NAME_FOR_EXTRA_GUARANTEE, () => null);
  const productImageForExtraGuarantee: Ref<string | null> = useState(PRODUCT_IMAGE_FOR_EXTRA_GUARANTEE, () => null);
  const productPriceForExtraGuarantee: Ref<number | null> = useState(PRODUCT_PRICE_FOR_EXTRA_GUARANTEE, () => null);
  const setProductNameForExtraGuarantee = (name: string) => {

    productNameForExtraGuarantee.value = name;
  
  };
  const setProductImageForExtraGuarantee = (name: string) => {

    productImageForExtraGuarantee.value = name;
  
  };
  const setProductPriceForExtraGuarantee = (fullProductPrice: number | undefined) => {

    productPriceForExtraGuarantee.value = fullProductPrice ?? null;

  };

  const setSkuForExtraGuarantee = (sku: string) => {

    skuForExtraGuarantee.value = sku;
  
  };

  const getItemFromSku = (sku: string): LineItem | undefined => {

    return getLineItemFromSku(cart.value, sku);
  
  };

  const productForExtraGuarantee = computed<ProductVariant | null>(() => {

    if (!skuForExtraGuarantee.value) {

      return null;

    }

    return getItemFromSku(skuForExtraGuarantee.value)?.variant || null;
  
  });

  const extraGuaranteeLineItem = computed(() => {

    return cart.value?.lineItems?.find(lineItem =>
      isGuaranteeLineItem(lineItem, skuForExtraGuarantee.value));
  
  });

  const isGuaranteeLineItem = (lineItem: LineItem, sku: ProductVariant['sku']) => {

    return sku && lineItem.custom?.customFieldsRaw?.[0]?.value === sku;
  
  };

  const skuHasAddedExtraGuarantee = (productSku: ProductVariant['sku']) => {

    return !!cart.value?.lineItems?.find((item: LineItem) =>
      isExtraGuaranteeItem(item, productSku),
    )?.custom?.customFieldsRaw?.[0]?.value;
  
  };

  const isExtraGuaranteeItem = (item: LineItem, productSku: ProductVariant['sku']) => {

    return item.variant?.sku === virtualProductSku &&
      item.custom?.customFieldsRaw?.[0]?.value === productSku &&
      item.productType?.key !== STANDARD_PRODUCT;
  
  };

  const cartItemExtraGuaranteeExcluded = (productSku: ProductVariant['sku']) => {

    return cart.value?.lineItems?.some((item: LineItem) => {

      if (item.variant?.sku === productSku) {

        return isGuaranteeExcluded(item.variant);
      
      }

      return false;
    
    },
    );
  
  };

  const extraGuaranteeParentSku = (product: LineItem) => {

    return product.custom?.customFieldsRaw?.[0]?.value;
  
  };

  const shouldAddExtraGuarantee = (product: LineItem) => {

    const sku = product.variant?.sku;

    return !skuHasAddedExtraGuarantee(sku) &&
      !isVirtualProduct(sku) &&
      !isOnPaymentPage.value &&
      !cartItemExtraGuaranteeExcluded(sku);
  
  };

  const isExtraGuaranteeActionsVisible = ({ sku }: { sku: string }) => {

    return isVirtualProduct(sku) && !isOnPaymentPage.value && !isThankYouPage.value;
  
  };

  const setExtraGuaranteeInfo = ({ fullProductPrice, name, image, sku }: SetExtraGuarenteeInfoParams) => {

    setSkuForExtraGuarantee(sku);
    setProductNameForExtraGuarantee(name);
    setProductImageForExtraGuarantee(image);
    setProductPriceForExtraGuarantee(fullProductPrice);

  };

  return {
    skuHasAddedExtraGuarantee,
    skuForExtraGuarantee: computed<string | null>(() => skuForExtraGuarantee.value),
    isVirtualProduct,
    extraGuaranteeParentSku,
    setExtraGuaranteeInfo,
    isExtraGuaranteeItem,
    shouldAddExtraGuarantee,
    cartItemExtraGuaranteeExcluded,
    productNameForExtraGuarantee,
    productImageForExtraGuarantee,
    productPriceForExtraGuarantee,
    getItemFromSku,
    productForExtraGuarantee,
    isExtraGuaranteeActionsVisible,
    extraGuaranteeLineItem,
  };

};
